import styles from "../../../../MapMonitoring/components/AddMarkerModal/AddIncidentModal.module.css";
import React, { useEffect, useMemo, useReducer } from "react";
import { ListItemText, MenuItem, Select, TextField } from "@mui/material";
import useAppSnackbar from "../../../../../hooks/useAppSnackbar";

import { counterpartyService } from "../../../../../services/guide/counterparty-service";
import { useFetchAllCompany } from "../../../../../hooks/guide/useFetchAllCompany";
import { useFetchGetCounterpartyById } from "../../../../../hooks/guide/useFetchGetCounterpartyById";
import { CreateCounterparty, UpdateCounterparty } from "../../../../../types/api/guide/counterparty.types";

type FormState = { name: string; companyId: string };

type Action =
    | { type: "SET_FIELD"; name: keyof FormState; value: any }
    | { type: "SET_All"; payload: FormState }
    | { type: "RESET" };

const initialState: FormState = { name: "", companyId: "" };

const reducer = (state: FormState, action: Action): FormState => {
    switch (action.type) {
        case "SET_FIELD":
            return { ...state, [action.name]: action.value };
        case "SET_All":
            return { ...action.payload };
        case "RESET":
            return initialState;
        default:
            return state;
    }
};

interface CounterpartyAddModalProps {
    onClose: () => void;
    refetchData: () => void;
    id?: string;
}

export const CounterpartyAddModal = ({ onClose, refetchData, id }: CounterpartyAddModalProps) => {
    const { showSuccess, showError, SnackbarComponent } = useAppSnackbar();
    const { data: companies = [] } = useFetchAllCompany();

    const { data: counterparty } = useFetchGetCounterpartyById(id);

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        if (!counterparty) return;

        dispatch({
            type: "SET_All",
            payload: {
                name: counterparty.name ?? "",
                companyId: counterparty.company ?? "",
            },
        });
    }, [counterparty]);

    const handleValue = (event: any) => {
        const { name, value } = event.target;
        dispatch({ type: "SET_FIELD", name, value });
    };

    const canSave = useMemo(() => {
        if (!state.name.trim()) return false;
        if (!state.companyId) return false;
        return true;
    }, [state]);

    const handleSave = async () => {
        try {
            if (!counterparty) {
                const payload: CreateCounterparty = { name: state.name.trim(), companyId: state.companyId };
                await counterpartyService.create(payload);
                showSuccess("Контрагент создан");
            } else {
                const payload: UpdateCounterparty = {
                    id: counterparty.id,
                    name: state.name.trim(),
                    companyId: state.companyId,
                };
                await counterpartyService.update(counterparty.id, payload);
                showSuccess("Контрагент обновлён");
            }

            refetchData();
        } catch (e: any) {
            showError(e?.response?.data ?? "Ошибка при обновлении/добавлении контрагента");
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>
                        {counterparty ? "Редактировать контрагента" : "Добавить контрагента"}
                    </h3>
                    <button className={styles.modalClose} onClick={onClose}>×</button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.modalField}>
                        <div className={styles.modalLabel}>Название</div>
                        <div className={styles.modalCoords}>
                            <TextField name="name" value={state.name} onChange={handleValue} fullWidth />
                        </div>
                    </div>

                    <div className={styles.modalField}>
                        <div className={styles.modalLabel}>Компания</div>
                        <div className={styles.modalCoords}>
                            <Select
                                name="companyId"
                                value={state.companyId}
                                onChange={handleValue}
                                fullWidth
                                displayEmpty
                                renderValue={(selected) =>
                                    companies.find((c: any) => c.id === selected)?.name ??
                                    (selected ? String(selected) : "Выберите компанию")
                                }
                                MenuProps={{ disablePortal: true, PaperProps: { sx: { zIndex: 20000 } } }}
                            >
                                {companies.map((c: any) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        <ListItemText primary={c.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.modalButton} onClick={onClose}>Отмена</button>
                    <button
                        className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
                        onClick={handleSave}
                        disabled={!canSave}
                        style={{ opacity: canSave ? 1 : 0.6 }}
                    >
                        Сохранить
                    </button>
                </div>
            </div>

            <SnackbarComponent />
        </div>
    );
};
